import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
  Select,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  InputAdornment,
  Toolbar,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  CheckCircle,
  Cancel,
  Refresh,
  Print,
  ArrowUpward,
  ArrowDownward,
  FilterListOff,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { serviceTemplateApi } from '../services/api';
import { ServiceTemplate, ServiceTemplateCreateRequest, ServiceTemplateUpdateRequest } from '../types/serviceTemplate';
import { SERVICE_TYPES } from '../types/serviceItem';
import { formatCurrency, CURRENCY_SYMBOL } from '../utils/formatters';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TableFilterCell } from '../components/TableFilterCell';
import { enumLabel } from '../i18n/translate';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';

export const ServicesManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ServiceTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Column filters and sorting
  const [columnFilters, setColumnFilters] = useState({
    serviceName: '',
    type: '',
    description: '',
    defaultPrice: '',
    autoAdd: '',
    status: '',
    sortOrder: '',
  });
  
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const queryClient = useQueryClient();

  // Fetch service templates
  const { data: templates = [], isLoading } = useQuery('serviceTemplates', serviceTemplateApi.getAll);

  // Create mutation
  const createMutation = useMutation(serviceTemplateApi.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('serviceTemplates');
      toast.success(t('services.messages.created'));
      setIsCreating(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('services.messages.createFailed'));
    },
  });

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: ServiceTemplateUpdateRequest }) => serviceTemplateApi.update(id, data),
    {
      onSuccess: (updatedTemplate) => {
        queryClient.invalidateQueries('serviceTemplates');
        toast.success(t('services.messages.updated'));
        loadTemplateIntoEditor(updatedTemplate);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('services.messages.updateFailed'));
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(serviceTemplateApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('serviceTemplates');
      toast.success(t('services.messages.deleted'));
      setOpenDeleteDialog(false);
      setSelectedTemplate(null);
      setEditMode(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('services.messages.deleteFailed'));
    },
  });

  // Form state
  const [formData, setFormData] = useState<ServiceTemplateCreateRequest>({
    serviceType: SERVICE_TYPES.OTHER,
    serviceName: '',
    description: '',
    defaultPrice: 0,
    isActive: true,
    autoAdd: false,
    allowPriceChange: false,
    sortOrder: 0,
  });

  const loadTemplateIntoEditor = (template: ServiceTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      serviceType: template.serviceType,
      serviceName: template.serviceName,
      description: template.description || '',
      defaultPrice: template.defaultPrice,
      isActive: template.isActive,
      autoAdd: template.autoAdd,
      allowPriceChange: template.allowPriceChange,
      sortOrder: template.sortOrder,
    });
    setIsCreating(false);
    setEditMode(true);
  };

  const resetForm = () => {
    setFormData({
      serviceType: SERVICE_TYPES.OTHER,
      serviceName: '',
      description: '',
      defaultPrice: 0,
      isActive: true,
      autoAdd: false,
      allowPriceChange: false,
      sortOrder: 0,
    });
    setSelectedTemplate(null);
    setIsCreating(false);
    setEditMode(false);
  };

  const handleNewService = () => {
    resetForm();
    setIsCreating(true);
    setEditMode(true);
    setSelectedTemplate(null);
  };

  const handleRowClick = (template: ServiceTemplate) => {
    // If clicking the same template, do nothing
    if (selectedTemplate?.id === template.id && !isCreating) {
      return;
    }

    loadTemplateIntoEditor(template);
  };

  const handleDelete = () => {
    if (selectedTemplate) {
      setOpenDeleteDialog(true);
    }
  };

  const handleConfirmDelete = () => {
    if (selectedTemplate) {
      deleteMutation.mutate(selectedTemplate.id.toString());
    }
  };

  const handleSave = () => {
    if (!formData.serviceName) {
      toast.error(t('services.validation.nameRequired'));
      return;
    }

    if (isCreating) {
      createMutation.mutate(formData);
    } else if (editMode && selectedTemplate) {
      updateMutation.mutate({ id: selectedTemplate.id.toString(), data: formData });
    }
  };

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
    setPage(0);
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' };
        } else {
          return null;
        }
      } else {
        return { key, direction: 'asc' };
      }
    });
  };

  // Filter templates based on column filters
  const filteredTemplates = templates.filter((template) => {
    const statusText = template.isActive ? 'active' : 'inactive';
    const autoAddText = template.autoAdd ? 'yes' : 'no';
    
    return (
      (!columnFilters.serviceName || template.serviceName.toLowerCase().includes(columnFilters.serviceName.toLowerCase())) &&
      (!columnFilters.type || template.serviceType.toLowerCase().includes(columnFilters.type.toLowerCase())) &&
      (!columnFilters.description || (template.description || '').toLowerCase().includes(columnFilters.description.toLowerCase())) &&
      (!columnFilters.defaultPrice || (template.defaultPrice?.toString() || '').includes(columnFilters.defaultPrice)) &&
      (!columnFilters.autoAdd || autoAddText.includes(columnFilters.autoAdd.toLowerCase())) &&
      (!columnFilters.status || statusText.includes(columnFilters.status.toLowerCase())) &&
      (!columnFilters.sortOrder || (template.sortOrder?.toString() || '').includes(columnFilters.sortOrder))
    );
  });

  // Sort filtered templates
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    if (!sortConfig) return 0;

    let aValue: any;
    let bValue: any;

    switch (sortConfig.key) {
      case 'serviceName':
        aValue = a.serviceName.toLowerCase();
        bValue = b.serviceName.toLowerCase();
        break;
      case 'type':
        aValue = a.serviceType.toLowerCase();
        bValue = b.serviceType.toLowerCase();
        break;
      case 'description':
        aValue = (a.description || '').toLowerCase();
        bValue = (b.description || '').toLowerCase();
        break;
      case 'defaultPrice':
        aValue = parseFloat(a.defaultPrice?.toString() || '0') || 0;
        bValue = parseFloat(b.defaultPrice?.toString() || '0') || 0;
        break;
      case 'autoAdd':
        aValue = a.autoAdd ? 1 : 0;
        bValue = b.autoAdd ? 1 : 0;
        break;
      case 'status':
        aValue = a.isActive ? 1 : 0;
        bValue = b.isActive ? 1 : 0;
        break;
      case 'sortOrder':
        aValue = a.sortOrder || 0;
        bValue = b.sortOrder || 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedTemplates = sortedTemplates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Sticky Toolbar with New, Save, Delete buttons - Connected to top of page */}
      <Box
        sx={{ 
          position: 'sticky',
          top: 64, // Position below AppBar (AppBar height is typically 64px)
          zIndex: 1100, // Higher than AppBar (which is 1100) to stay on top
          bgcolor: 'background.paper', 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          width: '100%'
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar 
            sx={{ 
              pl: { xs: 2, sm: 3 }, 
              pr: { xs: 2, sm: 3 }, 
              minHeight: '48px !important', 
              py: 0.5
            }}
          >
            {/* Action Buttons - New, Save, Delete */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewService}
                color="primary"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('common.new')}</Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!isCreating && !editMode}
                color="primary"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  opacity: (isCreating || editMode) ? 1 : 0.6,
                  '&.Mui-disabled': {
                    opacity: 0.4
                  }
                }}
              >{t('common.save')}</Button>
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={!selectedTemplate}
                color="error"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('common.delete')}</Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          {/* Main Table Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 0 }}>
              {/* Secondary Toolbar - Refresh, Export, Print, Pagination, Clear Filters */}
              <Toolbar sx={{ 
                pl: { xs: 2, sm: 3 }, 
                pr: { xs: 2, sm: 3 }, 
                minHeight: '48px !important', 
                py: 0.5, 
                bgcolor: 'background.paper', 
                borderBottom: '1px solid', 
                borderColor: 'divider' 
              }}>
                {/* Secondary Action Buttons - Left */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh fontSize="small" />}
                    onClick={() => queryClient.invalidateQueries('serviceTemplates')}
                    size="small"
                    sx={{ 
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      '& .MuiButton-startIcon': {
                        marginRight: 0.5
                      }
                    }}
                  >{t('common.refresh')}</Button>
                  <Button
                    variant="outlined"
                    startIcon={<Print fontSize="small" />}
                    size="small"
                    sx={{ 
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      '& .MuiButton-startIcon': {
                        marginRight: 0.5
                      }
                    }}
                  >{t('common.print')}</Button>
                </Box>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Pagination Controls - Center */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage(prev => Math.max(0, prev - 1))}
                  disabled={page === 0}
                  sx={{ 
                    width: '28px',
                    height: '28px',
                    '&.Mui-disabled': { opacity: 0.3 }
                  }}
                >
                  <ArrowUpward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                </IconButton>
                <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', minWidth: '80px', textAlign: 'center' }}>
                {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredTemplates.length)} {t('common.of')} {filteredTemplates.length}
                </Typography>
                
                {/* Page Numbers */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {Array.from({ length: Math.min(5, Math.ceil(filteredTemplates.length / rowsPerPage)) }, (_, i) => {
                    const totalPages = Math.ceil(filteredTemplates.length / rowsPerPage);
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i;
                    } else if (page < 2) {
                      pageNum = i;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 5 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    if (pageNum < 0 || pageNum >= totalPages) return null;

                    return (
                      <Button
                        key={pageNum}
                        size="small"
                        onClick={() => setPage(pageNum)}
                        sx={{
                          minWidth: '32px',
                          height: '28px',
                          px: 1,
                          fontSize: '0.8125rem',
                          bgcolor: page === pageNum ? 'primary.main' : 'transparent',
                          color: page === pageNum ? 'white' : 'text.primary',
                          '&:hover': {
                            bgcolor: page === pageNum ? 'primary.dark' : 'action.hover'
                          }
                        }}
                      >
                        {pageNum + 1}
                      </Button>
                    );
                  })}
                  {Math.ceil(filteredTemplates.length / rowsPerPage) > 5 && page < Math.ceil(filteredTemplates.length / rowsPerPage) - 3 && (
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', px: 0.5 }}>
                      ...
                    </Typography>
                  )}
                  {Math.ceil(filteredTemplates.length / rowsPerPage) > 5 && page < Math.ceil(filteredTemplates.length / rowsPerPage) - 3 && (
                    <Button
                      size="small"
                      onClick={() => setPage(Math.ceil(filteredTemplates.length / rowsPerPage) - 1)}
                      sx={{
                        minWidth: '32px',
                        height: '28px',
                        px: 1,
                        fontSize: '0.8125rem',
                      }}
                    >
                      {Math.ceil(filteredTemplates.length / rowsPerPage)}
                    </Button>
                  )}
                </Box>

                <IconButton
                  size="small"
                  onClick={() => setPage(prev => Math.min(Math.ceil(filteredTemplates.length / rowsPerPage) - 1, prev + 1))}
                  disabled={page >= Math.ceil(filteredTemplates.length / rowsPerPage) - 1}
                  sx={{ 
                    width: '28px',
                    height: '28px',
                    '&.Mui-disabled': { opacity: 0.3 }
                  }}
                >
                  <ArrowDownward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                </IconButton>
                
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  size="small"
                  sx={{ 
                    height: '28px',
                    fontSize: '0.8125rem',
                    ml: 1,
                    '& .MuiSelect-select': { py: 0.5, px: 1 }
                  }}
                  renderValue={(value) => t('common.rowsPerPage', { count: value, defaultValue: `${value} / page` })}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </Box>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Clear Filters Button - Right */}
              <Button
                size="small"
                startIcon={<FilterListOff fontSize="small" />}
                onClick={() => {
                  setColumnFilters({
                    serviceName: '',
                    type: '',
                    description: '',
                    defaultPrice: '',
                    autoAdd: '',
                    status: '',
                    sortOrder: '',
                  });
                  setSortConfig(null);
                  setPage(0);
                }}
                sx={{ fontSize: '0.8125rem', height: '28px', px: 1 }}
              >{t('common.clearFilters')}</Button>
            </Toolbar>

            {/* Table */}
            <TableContainer>
              <Table 
                size="small" 
                sx={{ 
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': { 
                    fontSize: '0.875rem',
                    px: { xs: 2, sm: 3 }
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableFilterCell placeholder={t('fields.serviceName')} value={columnFilters.serviceName} onChange={(value) => handleColumnFilterChange('serviceName', value)} onSort={() => handleSort('serviceName')} sortDirection={sortConfig?.key === 'serviceName' ? sortConfig.direction : null} width="180px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.type')} value={columnFilters.type} onChange={(value) => handleColumnFilterChange('type', value)} onSort={() => handleSort('type')} sortDirection={sortConfig?.key === 'type' ? sortConfig.direction : null} width="130px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.description')} value={columnFilters.description} onChange={(value) => handleColumnFilterChange('description', value)} onSort={() => handleSort('description')} sortDirection={sortConfig?.key === 'description' ? sortConfig.direction : null} width="250px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.defaultPrice')} value={columnFilters.defaultPrice} onChange={(value) => handleColumnFilterChange('defaultPrice', value)} onSort={() => handleSort('defaultPrice')} sortDirection={sortConfig?.key === 'defaultPrice' ? sortConfig.direction : null} width="130px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.autoAdd')} value={columnFilters.autoAdd} onChange={(value) => handleColumnFilterChange('autoAdd', value)} onSort={() => handleSort('autoAdd')} sortDirection={sortConfig?.key === 'autoAdd' ? sortConfig.direction : null} width="120px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.status')} value={columnFilters.status} onChange={(value) => handleColumnFilterChange('status', value)} onSort={() => handleSort('status')} sortDirection={sortConfig?.key === 'status' ? sortConfig.direction : null} width="120px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.sortOrder')} value={columnFilters.sortOrder} onChange={(value) => handleColumnFilterChange('sortOrder', value)} onSort={() => handleSort('sortOrder')} sortDirection={sortConfig?.key === 'sortOrder' ? sortConfig.direction : null} width="120px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : paginatedTemplates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                        {t('management.emptyServices')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTemplates.map((template) => (
                      <TableRow
                        key={template.id}
                        hover
                        selected={selectedTemplate?.id === template.id}
                        onClick={() => handleRowClick(template)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '180px', width: '180px' }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {template.serviceName}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '130px', width: '130px' }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {enumLabel(t, 'serviceTypes', template.serviceType)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '250px', width: '250px' }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {template.description || '-'}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '130px', width: '130px' }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {formatCurrency(template.defaultPrice)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '120px', width: '120px' }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {template.autoAdd ? (
                              <CheckCircle color="success" fontSize="small" />
                            ) : (
                              <Cancel color="disabled" fontSize="small" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '120px', width: '120px' }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            <Chip
                              label={template.isActive ? t('status.active') : t('status.inactive')}
                              size="small"
                              color={template.isActive ? 'success' : 'default'}
                              sx={{
                                maxWidth: '100%',
                                '& .MuiChip-label': {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '100%'
                                }
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, maxWidth: '120px', width: '120px' }}>
                          <Box sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {template.sortOrder}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Detail Panel */}
        {(isCreating || selectedTemplate) && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ mt: 2, p: 1, display: 'flex', flexDirection: 'column', ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">
                  {isCreating ? t('services.newService') : `${selectedTemplate?.serviceName || ''}`}
                </Typography>
                {selectedTemplate && !isCreating && (
                  <Chip
                    label={selectedTemplate.isActive ? t('status.active') : t('status.inactive')}
                    size="small"
                    color={selectedTemplate.isActive ? 'success' : 'default'}
                  />
                )}
              </Box>
              <Divider sx={{ mb: 1 }} />
              
              <Grid container spacing={1}>
                {/* Line 1: Type | Name | Order | Price */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>{t('fields.type')}</InputLabel>
                    <Select
                      value={formData.serviceType}
                      label={t('fields.type')}
                      onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                      sx={{ '& .MuiSelect-select': { py: 1 } }}
                    >
                      <MenuItem value={SERVICE_TYPES.RENTAL}>{t('serviceTypes.rental')}</MenuItem>
                      <MenuItem value={SERVICE_TYPES.DELIVERY}>{t('serviceTypes.delivery')}</MenuItem>
                      <MenuItem value={SERVICE_TYPES.PICKUP}>{t('serviceTypes.pickup')}</MenuItem>
                      <MenuItem value={SERVICE_TYPES.CLEANING}>{t('serviceTypes.cleaning')}</MenuItem>
                      <MenuItem value={SERVICE_TYPES.FUEL_SERVICE}>{t('serviceTypes.fuelService')}</MenuItem>
                      <MenuItem value={SERVICE_TYPES.VALET}>{t('serviceTypes.valet')}</MenuItem>
                      <MenuItem value={SERVICE_TYPES.OTHER}>{t('serviceTypes.other')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('fields.name')}
                    value={formData.serviceName}
                    onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
                    required
                    placeholder={t('services.examplePlaceholder')}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('fields.order')}
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('fields.price')}
                    type="number"
                    value={formData.defaultPrice}
                    onChange={(e) => setFormData({ ...formData, defaultPrice: parseFloat(e.target.value) || 0 })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment>,
                    }}
                    required
                  />
                </Grid>
                {/* Line 2: Description */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('fields.description')}
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                    }
                    label={t('status.active')}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.autoAdd}
                        onChange={(e) => setFormData({ ...formData, autoAdd: e.target.checked })}
                      />
                    }
                    label={t('services.autoAddToReservations')}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formData.allowPriceChange}
                        onChange={(e) => setFormData({ ...formData, allowPriceChange: e.target.checked })}
                      />
                    }
                    label={t('services.allowPriceChange')}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('management.deleteService')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('dialogs.deleteBody', { item: selectedTemplate?.serviceName || t('fields.service') })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isLoading}
          >{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};
